import React, { useEffect, useState } from 'react'
import api from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import { ISyncResource, IIntegrationSyncResourceTypes, IProject } from 'types'
import Select from 'components/select'
import { IntegrationSyncResourceTypes, PROJECT } from 'app-constants'
import { capitaliseFirstLetter } from 'helpers/utils'
import Checkbox from 'components/checkbox'

interface MortaIntegrationProps {
  type: IIntegrationSyncResourceTypes
  selectedProjects: string[]
  setSelectedProjects: (projects: string[]) => void
  selectedViews: string[]
  setSelectedViews: (views: string[]) => void
  syncAllHubs: boolean
  setSyncAllHubs: (value: boolean) => void
  compact: boolean
}

const Morta: React.FC<MortaIntegrationProps> = ({
  type,
  selectedProjects,
  setSelectedProjects,
  selectedViews,
  setSelectedViews,
  syncAllHubs,
  setSyncAllHubs,
  compact = false
}) => {
  const { displayErrorMessage } = useApplicationStore()

  const [projects, setProjects] = useState<ISyncResource[]>()
  const [loadingProjects, setLoadingProjects] = useState<boolean>(false)

  const [enteredViewIds, setEnteredViewIds] = useState<string>('')

  useEffect(() => {
    if (type === IntegrationSyncResourceTypes.VIEWS) {
      const joined = (selectedViews ?? []).join(', ')
      const inputNormalized = enteredViewIds.replace(/,\s*$/, '')
      if (joined !== inputNormalized) {
        setEnteredViewIds(joined)
      }
    }
  }, [type, selectedViews])

  useEffect(() => {
    if (type !== IntegrationSyncResourceTypes.VIEWS) {
      setLoadingProjects(true)
      api
        .getOwnerProjects()
        .then((response) => {
          const projects = response.data
            .filter((project: IProject) => !project.isDeleted)
            .map((project) => ({
              id: project.publicId,
              name: project.name
            }))
          setProjects(projects)
          setLoadingProjects(false)
        })
        .catch((error) => {
          displayErrorMessage(error)
          setLoadingProjects(false)
        })
    }
  }, [type, displayErrorMessage])

  const projectOptions = (projects ?? [])
    .slice()
    .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
    .map((p) => ({ label: p.name, value: p.id }))

  return (
    <div>
      {type === IntegrationSyncResourceTypes.VIEWS ? (
        <div className={`${compact ? 'mt-0' : 'mt-[30px]'}`}>
          <div className="font-bold flex mb-2.5">
            Enter Morta View Public IDs<span className="text-red">*</span>
          </div>
          <input
            type="text"
            placeholder="Enter Morta View Public IDs separated by commas"
            className="input"
            onChange={(e) => {
              const input = e.target.value
              const viewIds = input
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id !== '')
              setSelectedViews(viewIds)
              setEnteredViewIds(input)
            }}
            value={enteredViewIds}
          />
        </div>
      ) : (
        <div className={`${compact ? 'mt-0' : 'mt-[30px]'}`}>
          <div className="mb-2.5 w-fit">
            <Checkbox
              checked={syncAllHubs}
              onChange={() => {
                const newValue = !syncAllHubs
                setSyncAllHubs(newValue)
                if (newValue) {
                  setSelectedProjects([])
                }
              }}
            >
              Sync all hubs
            </Checkbox>
          </div>
          {!syncAllHubs && (
            <div>
              <div className="font-bold flex mb-2.5">
                Select Morta {capitaliseFirstLetter(PROJECT)}s<span className="text-red">*</span>
              </div>
              <Select
                options={projectOptions}
                onOptionClick={(option) => {
                  const newOptions = [...selectedProjects]
                  const indexOf = newOptions.findIndex((value) => value === option)
                  if (indexOf === -1) newOptions.push(option)
                  else newOptions.splice(indexOf, 1)
                  setSelectedProjects(newOptions)
                }}
                optionsSelected={selectedProjects}
                setOptionsSelected={setSelectedProjects}
                loading={loadingProjects}
                multiselect
                disabled={syncAllHubs}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Morta
